import React from 'react';
import { Badge } from '@parsina/framework';
import { C } from '../../../constants/theme';
import { useTranslation } from '../../../i18n/I18nProvider.jsx';
import Panel from '../../../components/common/Panel.jsx';
import { conditionStatusKey } from '../../../lib/patients.js';

// سوابق — what has been recorded about this patient, read-only.
//
// Conditions and medications come from clinicalencounter and are keyed on
// `appointment_id`: there is no encounters table on this platform, encounters
// ARE appointments. Nothing here groups by "visit" for that reason — inventing
// a visit grouping the data does not carry would be inventing a fact.

const dosageLine = (medication) => {
  const d = medication?.dosageInstruction;
  if (!d) return '';
  if (typeof d === 'string') return d;
  if (d.text) return d.text;
  return [d.dose, d.frequency, d.duration].filter(Boolean).join(' · ');
};

const HistoryTab = ({ patient }) => {
  const { t, formatDate } = useTranslation();
  const conditions = patient.conditions || [];
  const medications = patient.medications || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Panel title={t('patient.history.conditions')}>
        {conditions.length === 0 ? (
          <div style={{ fontSize: 13, color: C.textMuted }}>{t('patient.history.noConditions')}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {conditions.map((c) => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: C.textPrimary }}>{c.display}</span>
                {c.code && (
                  <span dir="ltr" style={{ unicodeBidi: 'isolate', fontSize: 11.5, color: C.textMuted }}>
                    {c.code}
                  </span>
                )}
                {/* Recognised FHIR status → label; unknown → verbatim. */}
                {c.clinicalStatus && (
                  <Badge variant="outline">
                    {conditionStatusKey(c.clinicalStatus)
                      ? t(conditionStatusKey(c.clinicalStatus))
                      : c.clinicalStatus}
                  </Badge>
                )}
                {c.onsetDate && (
                  <span style={{ fontSize: 12, color: C.textMuted, marginInlineStart: 'auto' }}>
                    {formatDate(c.onsetDate, 'numeric')}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title={t('patient.history.medications')}>
        {medications.length === 0 ? (
          <div style={{ fontSize: 13, color: C.textMuted }}>{t('patient.history.noMedications')}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {medications.map((m) => (
              <div key={m.id} style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: C.textPrimary }}>{m.display}</span>
                <span style={{ fontSize: 12.5, color: C.textSecondary }}>{dosageLine(m)}</span>
                {m.authoredOn && (
                  <span style={{ fontSize: 12, color: C.textMuted, marginInlineStart: 'auto' }}>
                    {t('patient.history.prescribedOn', { date: formatDate(m.authoredOn, 'numeric') })}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
};

export default HistoryTab;
